import loadable from "@loadable/component";
import React from "react";
import { Router, Route, Switch, Redirect } from "react-router-dom";

import { browserHistory } from "js/react/history";
import { FirebaseAuthProvider } from "js/react/views/Auth/FirebaseAuthContext";
import { FirebaseAuthInitalizer } from "js/react/views/Auth/FirebaseAuthInitalizer";
import { Authenticate } from "js/react/views/Auth/Authenticate";
import { serverContext } from "js/config";
import { GoogleAuthProvider } from "js/react/views/Auth/GoogleAuthContext";
import { GoogleAuthInitalizer } from "js/react/views/Auth/GoogleAuthInitalizer";
import PlayerServerContext from "./PlayerServerContext";
import SsoConsume from "./SsoConsume";
import Logout from "./Logout";

const Loading = () => {
    return (
        // TODO: spinner
        <div></div>
    );
};

const LoadableEditorAndLibrary = loadable(() => import(/* webpackChunkName: "editor" */ "./EditorAndLibrary"), {
    fallback: <Loading />
});

const LoadablePlayer = loadable(() => import(/* webpackChunkName: "player" */ "./Player"), {
    fallback: <Loading />
});

const LoadablePresenter = loadable(() => import(/* webpackChunkName: "presenter" */ "./Presenter"), {
    fallback: <Loading />
});

const LoadableMeeting = loadable(() => import(/* webpackChunkName: "meeting" */ "./Meeting"), {
    fallback: <Loading />
});

const LoadableBonus = loadable(() => import(/* webpackChunkName: "bonus" */ "./Bonus"), {
    fallback: <Loading />
});

const LoadableCheckout = loadable(() => import(/* webpackChunkName: "checkout" */ "./Checkout"), {
    fallback: <Loading />
});

const LoadableCopyDeck = loadable(() => import(/* webpackChunkName: "copyDeck" */ "./CopyDeck"), {
    fallback: <Loading />
});

const LoadableInvite = loadable(() => import(/* webpackChunkName: "invite" */ "./Invite"), {
    fallback: <Loading />
});

const LoadableUiHandler = loadable(() => import(/* webpackChunkName: "gdrive" */ "js/react/views/GDrive/UiHandler"), {
    fallback: <Loading />
});

const LoadableSlackRedirectHandler = loadable(() => import(/* webpackChunkName: "slack" */ "js/react/views/Slack/RedirectHandler"), {
    fallback: <Loading />
});

export default function App() {
    return withProviders(
        <Router history={browserHistory}>
            <Switch>
                <Route path={"/signup"}>
                    <Authenticate defaultPage="createAccount">
                        <Redirect to="/" />
                    </Authenticate>
                </Route>
                <Route path={"/login"}>
                    <Authenticate defaultPage="signIn">
                        <Redirect to="/" />
                    </Authenticate>
                </Route>
                <Route path={"/logout"}>
                    <GoogleAuthInitalizer>
                        <FirebaseAuthInitalizer>
                            <Logout />
                        </FirebaseAuthInitalizer>
                    </GoogleAuthInitalizer>
                </Route>
                <Route exact path={["/", "/:id([A-Za-z0-9_-]{20})/:slideNumber?"]}>
                    {/* backbone router handles both the editor and library routes */}
                    <Authenticate defaultPage="signIn">
                        <LoadableEditorAndLibrary />
                    </Authenticate>
                </Route>
                <Route path={["/player", "/deck"]}>
                    {/* note: the player relies upon invites to do its auth */}
                    <GoogleAuthInitalizer>
                        <FirebaseAuthInitalizer>
                            <PlayerServerContext>
                                <LoadablePlayer />
                            </PlayerServerContext>
                        </FirebaseAuthInitalizer>
                    </GoogleAuthInitalizer>
                </Route>
                <Route path={"/presenter"}>
                    <GoogleAuthInitalizer>
                        <FirebaseAuthInitalizer>
                            <LoadablePresenter />
                        </FirebaseAuthInitalizer>
                    </GoogleAuthInitalizer>
                </Route>
                <Route path={"/meeting"}>
                    <GoogleAuthInitalizer>
                        <FirebaseAuthInitalizer>
                            <PlayerServerContext>
                                <LoadableMeeting />
                            </PlayerServerContext>
                        </FirebaseAuthInitalizer>
                    </GoogleAuthInitalizer>
                </Route>
                <Route path={"/setBonus/:type"}>
                    <Authenticate defaultPage="signIn">
                        <LoadableBonus />
                    </Authenticate>
                </Route>
                <Route path={"/checkout"}>
                    <Authenticate defaultPage="createAccount">
                        <LoadableCheckout />
                    </Authenticate>
                </Route>
                <Route path={"/copyDeck/:presentationId/:name?"}>
                    <Authenticate defaultPage="createAccount">
                        <LoadableCopyDeck />
                    </Authenticate>
                </Route>
                <Route path={["/invite/:token", "/teaminvite/:token"]}>
                    <Authenticate
                        defaultPage={serverContext.form == "login" ? "signIn" : "createAccount"}
                        email={serverContext.email}
                    >
                        <LoadableInvite />
                    </Authenticate>
                </Route>
                <Route path={"/api/sso/saml/consume"}>
                    <GoogleAuthInitalizer>
                        <FirebaseAuthInitalizer>
                            <SsoConsume />
                        </FirebaseAuthInitalizer>
                    </GoogleAuthInitalizer>
                </Route>
                <Route path={"/gdrive/uihandler"}>
                    <Authenticate defaultPage="signIn" showOnlyGoogle>
                        <LoadableUiHandler />
                    </Authenticate>
                </Route>
                <Route path={"/slack/redirect"}>
                    <Authenticate defaultPage="signIn">
                        <LoadableSlackRedirectHandler />
                    </Authenticate>
                </Route>
                <Route path="*">
                    {({ match }) => (
                        <h1 style={{ color: "red" }}>No &lt;Route&gt; for <code>{match.url}</code></h1>
                    )}
                </Route>
            </Switch>
        </Router>
    );
}

function withProviders(children) {
    return (
        <GoogleAuthProvider>
            <FirebaseAuthProvider>
                {children}
            </FirebaseAuthProvider>
        </GoogleAuthProvider>
    );
}
